import { useFlags } from "launchdarkly-react-client-sdk";
import { useEntitlements } from "src/hooks/use-entitlement";
import { NavSection } from "src/components/layout/nav/nav-section";

import { Icon } from "src/components/layout/nav/icons/customer-studio";
import type { ActiveSectionProps } from "src/components/layout/nav/utils";
import { useIsAgentsOnlyWorkspace } from "src/components/layout/nav/sections/hooks/agents";
import { useHasProductTabs } from "src/components/layout/nav/sections/hooks/projects";
import type { UseSectionHook } from "./types";

export const useCustomerStudioSection: UseSectionHook = () => {
  const { data: entitlementsData } = useEntitlements(false);
  const {
    appPerformancePage,
    appDestinationRulesEnabled,
    audienceTemplatesEnabled,
    appEnableGartnerDemo,
    appEnableProfileExplorer,
  } = useFlags();
  const { isAgentsOnly } = useIsAgentsOnlyWorkspace();
  const hasProductTabs = useHasProductTabs();
  // The setup pages still link to the legacy schema pages, which the product nav places under Schema.
  const schemaLink = {
    href: "/schema-v2",
    label: "Schema",
    activePaths: hasProductTabs ? ["/schema"] : undefined,
  };

  const hasAudiencesEntitlement = entitlementsData.entitlements.audiences;

  if (isAgentsOnly) {
    // Agents users who don't have audiences entitlement will only see the schema link
    return {
      title: "Customer Studio",
      isEnabled: true,
      links: [schemaLink],
    };
  }

  return {
    title: "Customer Studio",
    isEnabled: hasAudiencesEntitlement,
    links: [
      {
        href: "/audiences",
        label: "Audiences",
      },
      entitlementsData.entitlements.journeys && {
        href: "/journeys",
        label: "Journeys",
      },
      (appEnableGartnerDemo || appEnableProfileExplorer) && {
        href: "/profiles",
        label: "Profiles",
      },
      {
        href: "/priority-lists",
        label: "Priority lists",
      },
      appPerformancePage && {
        href: "/performance",
        label: "Performance",
      },
      {
        href: "/traits",
        label: "Traits",
      },
      schemaLink,
      appDestinationRulesEnabled && {
        href: "/governance",
        label: "Governance",
      },
      audienceTemplatesEnabled && {
        href: "/templates",
        label: "Templates",
      },
    ],
  };
};

export const CustomerStudio = (props: ActiveSectionProps) => {
  const section = useCustomerStudioSection();
  return <NavSection icon={Icon} {...section} {...props} />;
};
